import type { Availability } from '@cadencia/contracts';
import { SAFETY_LIMITS } from '../safety/limits.js';

/**
 * Cuándo puede entrenar el atleta (docs/TEST_INICIAL.md §2.2).
 *
 * Antes del test inicial esto no existía: el número de sesiones salía de la
 * plantilla y los días estaban fijos en el cliente —lunes, martes, jueves y
 * viernes—. Ahora lo declara el atleta, y lo que declara pasa por los mismos
 * límites de seguridad que cualquier otra cosa: el test no ofrece nada que los
 * supere y esto lo vuelve a comprobar, porque un perfil puede llegar por otro
 * camino (una importación, otro dispositivo).
 */

/** Días ISO: 1 = lunes … 7 = domingo. */
export const ETIQUETA_DIA: Record<number, string> = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo',
};

/**
 * El reparto que el test propone al elegir el número de sesiones. Separa los
 * días todo lo que se puede, que es lo que da recuperación entre sesiones del
 * mismo músculo. El de cuatro es el que el motor usaba fijo.
 */
export const DIAS_SUGERIDOS: Record<number, readonly number[]> = {
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 4, 5, 6],
};

export const MIN_SESIONES = 2;

/**
 * La racha más larga de días seguidos con sesión, contando la semana como un
 * círculo: entrenar sábado, domingo y lunes son tres días seguidos aunque el
 * lunes sea de la semana siguiente, y el cuerpo no sabe de semanas.
 */
export function longestRun(days: readonly number[]): number {
  const marcados = new Set(days);
  if (marcados.size === 0) return 0;
  if (marcados.size === 7) return 7;
  let mejor = 0;
  for (const inicio of marcados) {
    // Solo se empieza a contar desde un día cuyo anterior NO tiene sesión.
    const anterior = inicio === 1 ? 7 : inicio - 1;
    if (marcados.has(anterior)) continue;
    let largo = 0;
    let dia = inicio;
    while (marcados.has(dia)) {
      largo += 1;
      dia = dia === 7 ? 1 : dia + 1;
    }
    mejor = Math.max(mejor, largo);
  }
  return mejor;
}

/** Qué incumple una disponibilidad. Vacío = vale. */
export function availabilityProblems(availability: Availability): string[] {
  const problemas: string[] = [];
  const { maxStrengthSessionsPerWeek, maxMinutes, maxConsecutiveHardDays } = SAFETY_LIMITS.session;

  if (availability.days.length < MIN_SESIONES) {
    problemas.push(`Hacen falta al menos ${String(MIN_SESIONES)} días.`);
  }
  if (availability.days.length > maxStrengthSessionsPerWeek) {
    problemas.push(`Como mucho ${String(maxStrengthSessionsPerWeek)} sesiones de fuerza a la semana.`);
  }
  if (availability.maxSessionMinutes > maxMinutes) {
    problemas.push(`Una sesión no pasa de ${String(maxMinutes)} minutos.`);
  }
  if (longestRun(availability.days) > maxConsecutiveHardDays) {
    problemas.push(
      `No más de ${String(maxConsecutiveHardDays)} días seguidos: hace falta un día de descanso entre medias.`,
    );
  }
  return problemas;
}

/**
 * Desplazamiento de cada sesión respecto al lunes de su semana, en orden.
 *
 * La sesión N de la semana cae en el N-ésimo día declarado. Sustituye a
 * `DAY_OFFSETS = [0, 1, 3, 4]`, que era la misma idea con los días escritos a
 * mano.
 */
export function dayOffsets(availability: Availability): number[] {
  return [...availability.days].sort((a, b) => a - b).map((dia) => dia - 1);
}
